import { useEffect, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import {
  Input,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { AuthShell, PasswordInput } from './components/AuthShell';
import { authBrandPanel } from './components/authBrandPanel';
import { normalizeHost } from './components/vscanner/normalize-host';
import { SsoNotice } from './components/SsoNotice';
import { PasswordStrengthMeter, scorePassword } from './components/PasswordStrengthMeter';
import { RegisterOtpStep } from './components/RegisterOtpStep';
import authCta from './RegisterPage.module.css';
import form from './components/AuthForm.module.css';
import { brand, brandCopyright } from '@/config/brand';
import { useAuth } from '@/auth/AuthProvider';
import { registerUser } from '@/api/auth-flow';
import { resolveAuthError } from '@/api/auth-error';
import { submitWebsiteLead } from '@/api/leads';
import { trackSignUp, trackWafFreeLead } from '@/lib/analytics-events';
import { PRODUCT_GROUP } from '@/lib/lead-product-group';
import { isBlockedEmail } from './blocked-email-domains';
import { BlockedEmailsLink } from './BlockedEmailsLink';
import {
  POSITION_OPTIONS,
  COMPANY_SIZE_OPTIONS,
  INTERESTED_SOLUTION_OPTIONS,
  OTHER_SOLUTION,
} from './waf-free-register-options';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FOOTER = brandCopyright();


interface LeadValues {
  name: string;
  phone: string;
  email: string;
  company: string;
  website: string;
}

/** sessionStorage key — persists the lead draft so entered info survives a
 *  back-to-edit (e.g. after an "email already exists" error) and a page reload. */
const DRAFT_KEY = 'partnercfg.businessRegister';
type Draft = Partial<
  LeadValues & {
    position: string;
    companySize: string;
    purpose: string;
    otherText: string;
    /** Scanned host already used to prefill Website, so a later edit is not overwritten. */
    scanHost: string;
  }
>;
function readDraft(): Draft {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Draft) : {};
  } catch {
    return {};
  }
}

/**
 * Business-account register — a B2B lead form (mirrors the campaign flow). Two
 * steps: (1) a lead form → DataCentral `/cms/submit` so sales captures the lead
 * even if abandoned; (2) set a password → `/partner/auth/register` → account +
 * auto-login. Terms are agreed on the account-type chooser upstream (RegisterPage),
 * so this form omits its own Terms gate. `onBack` returns to that chooser.
 */
export function BusinessLeadRegister({ onBack }: { onBack?: () => void }) {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [step, setStep] = useState<'lead' | 'password' | 'otp'>('lead');
  const [error, setError] = useState<string | null>(null);

  // Restore any saved draft once (survives back-to-edit + reload).
  const draftRef = useRef<Draft | null>(null);
  if (draftRef.current === null) draftRef.current = readDraft();
  const d = draftRef.current;

  // The VScanner banner hands over the scanned domain as ?host= — prefill Website once.
  const [searchParams] = useSearchParams();
  const scanHost = normalizeHost(searchParams.get('host') ?? '').host;
  const prefillWebsite = !!scanHost && d.scanHost !== scanHost;

  const { register, handleSubmit, watch, formState: { errors } } = useForm<LeadValues>({
    defaultValues: {
      name: d.name ?? '', phone: d.phone ?? '', email: d.email ?? '',
      company: d.company ?? '',
      website: prefillWebsite ? `https://${scanHost}` : (d.website ?? ''),
    },
  });
  const [position, setPosition] = useState(d.position ?? '');
  const [companySize, setCompanySize] = useState(d.companySize ?? '');
  const [purpose, setPurpose] = useState(d.purpose ?? '');
  const [otherText, setOtherText] = useState(d.otherText ?? '');
  const [lead, setLead] = useState<LeadValues | null>(null);

  // Password step.
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const leadMut = useMutation({ mutationFn: submitWebsiteLead });
  const registerMut = useMutation({ mutationFn: registerUser });

  // Persist the draft on every change so nothing is lost going back or on reload.
  const watched = watch();
  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...watched, position, companySize, purpose, otherText, scanHost: scanHost ?? d.scanHost }));
    } catch {
      // best-effort
    }
  }, [watched, position, companySize, purpose, otherText, scanHost, d.scanHost]);

  const onLead = async (v: LeadValues) => {
    setError(null);
    if (!position || !companySize) {
      setError('Please select your position and company size.');
      return;
    }
    try {
      await leadMut.mutateAsync({
        firstname: v.name.trim(),
        email: v.email.trim(),
        phone: v.phone.trim(),
        company: v.company.trim(),
        position,
        companySize,
        website: v.website.trim(),
        // This is the WAF-free campaign → the interested service is WAF (VNIS).
        // The "purpose of use" is intent, not a service, so it rides in the note.
        interestedSolutions: [PRODUCT_GROUP.waf],
        specificNeeds:
          (purpose === OTHER_SOLUTION ? otherText.trim() : purpose) || undefined,
      });
      trackWafFreeLead(purpose ? 1 : 0);
      setLead(v);
      setStep('password');
    } catch {
      setError('Could not submit your information. Please try again.');
    }
  };

  // Validate the password, then go to email verification. The account is created
  // only after the OTP is confirmed (completeRegister), so fake/unowned business
  // emails can't complete signup.
  const onPassword = () => {
    setError(null);
    if (!lead) return;
    if (scorePassword(password) < 3) {
      setError('Password does not meet the requirements above.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setStep('otp');
  };

  // Create the account with the verified OTP. Returns an error message for the
  // OTP step to show, or null on success (then we navigate away).
  const completeRegister = async (otp: string): Promise<string | null> => {
    if (!lead) return 'Something went wrong. Please go back and try again.';
    try {
      const res = await registerMut.mutateAsync({
        type: 'business',
        email: lead.email.trim(),
        password,
        password_confirmation: confirm,
        company_name: lead.company.trim(),
        phone: lead.phone.trim(),
        contact_person: lead.name.trim(),
        country: 'vn',
        otp,
      });
      if (res.errors?.length) return res.errors[0].message;
      trackSignUp('business');
      try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* best-effort */ }
      if (res.data?.access_token && setSession(res.data, res.data.auth_method ?? 'local')) {
        navigate('/', { replace: true });
      } else {
        navigate('/auth/login', { replace: true });
      }
      return null;
    } catch (e) {
      return resolveAuthError(e, 'Registration failed. Please try again.');
    }
  };

  // Redesigned split layout (branded panel left, form right) — shared across steps.
  // No cross-link on these sub-forms (design shows only the theme toggle).
  const brandPanel = authBrandPanel();

  if (step === 'otp') {
    return (
      <AuthShell eyebrow="verify email" title="Verify your email" subtitle={lead?.email}
        footer={FOOTER} brandPanel={brandPanel}>
        <RegisterOtpStep
          email={lead?.email.trim() ?? ''}
          onVerify={completeRegister}
          onBack={() => {
            setError(null);
            setStep('password');
          }}
        />
      </AuthShell>
    );
  }

  if (step === 'password') {
    return (
      <AuthShell eyebrow="business" title="Set your password" subtitle={lead?.email} footer={FOOTER}
        brandPanel={brandPanel}>
        <div className={`${form.stack} ${authCta.pwStep} ${authCta.formEndLg}`}>
          {error && <Alert tone="danger">{error}</Alert>}
          <Field
            label="Password"
            required
            className={form.field}
            description="At least 8 characters, with one uppercase letter, one number, and one symbol."
          >
            {(p) => (
              <>
                <PasswordInput {...p} autoComplete="new-password" value={password}
                  wrapperClassName={`${form.input} ${form.tall}`}
                  onChange={(e) => setPassword(e.target.value)} />
                <PasswordStrengthMeter value={password} />
              </>
            )}
          </Field>
          <Field label="Confirm password" required className={form.field}>
            {(p) => (
              <PasswordInput {...p} autoComplete="new-password" value={confirm}
                wrapperClassName={`${form.input} ${form.tall}`}
                onChange={(e) => setConfirm(e.target.value)} />
            )}
          </Field>
          <div className={`${authCta.actions} ${authCta.actionsSpread}`}>
            <Button type="button" variant="ghost" size="lg" className={authCta.textBack} startIcon={<ArrowLeft size={15} />}
              onClick={() => { setError(null); setStep('lead'); }}>
              Back to edit info
            </Button>
            <Button type="button" size="lg" className={`${authCta.ctaGradient} ${authCta.ctaCompact}`} onClick={onPassword}>
              Continue
            </Button>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="business"
      title={`Sign up to ${brand.legalName}`}
      subtitle="Fill in your details to create an account and start protecting your website."
      compact
      footer={FOOTER}
      brandPanel={brandPanel}
    >
      <form onSubmit={handleSubmit(onLead)} className={`${form.stack} ${authCta.formEnd}`}>
        <div className={authCta.callout}>
          <SsoNotice />
        </div>
        {error && <Alert tone="danger">{error}</Alert>}
        <Field label="Name" required className={form.field} error={errors.name?.message}>
          {(p) => <Input {...p} autoComplete="name" wrapperClassName={form.input} invalid={!!errors.name}
            {...register('name', { required: 'Name is required.' })} />}
        </Field>
        <Field label="Phone number" required className={form.field} error={errors.phone?.message}>
          {(p) => <Input {...p} type="tel" autoComplete="tel" wrapperClassName={form.input} invalid={!!errors.phone}
            {...register('phone', { required: 'Phone number is required.' })} />}
        </Field>
        <Field
          label="Work email"
          required
          className={form.field}
          error={errors.email?.message}
          description={<>Free and temporary emails are blocked. See our blacklist <BlockedEmailsLink />.</>}
        >
          {(p) => <Input {...p} type="email" autoComplete="email" wrapperClassName={form.input} invalid={!!errors.email}
            {...register('email', {
              required: 'Email is required.',
              pattern: { value: EMAIL_RE, message: 'Email is invalid.' },
              validate: (v) =>
                !isBlockedEmail(v) ||
                'Free and temporary email providers are not allowed. Please use your company email.',
            })} />}
        </Field>
        <Field label="Position" required className={form.field}>
          {(p) => (
            <Select value={position} onValueChange={setPosition}>
              <SelectTrigger id={p.id} size="lg" className={form.select} aria-label="Position"><SelectValue placeholder="Select one" /></SelectTrigger>
              <SelectContent>
                {POSITION_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Company name" required className={form.field} error={errors.company?.message}>
          {(p) => <Input {...p} autoComplete="organization" wrapperClassName={form.input} invalid={!!errors.company}
            {...register('company', { required: 'Company name is required.' })} />}
        </Field>
        <Field label="Website" required className={form.field} error={errors.website?.message}>
          {(p) => <Input {...p} type="url" placeholder="https://" wrapperClassName={form.input} invalid={!!errors.website}
            {...register('website', { required: 'Website is required.' })} />}
        </Field>
        <Field label="Company size" required className={form.field}>
          {(p) => (
            <Select value={companySize} onValueChange={setCompanySize}>
              <SelectTrigger id={p.id} size="lg" className={form.select} aria-label="Company size"><SelectValue placeholder="Select one" /></SelectTrigger>
              <SelectContent>
                {COMPANY_SIZE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Purpose of use" className={form.field}>
          {(p) => (
            <div className={authCta.purposeStack}>
              <Select value={purpose} onValueChange={setPurpose}>
                <SelectTrigger id={p.id} size="lg" className={form.select} aria-label="Purpose of use"><SelectValue placeholder="Select one" /></SelectTrigger>
                <SelectContent>
                  {INTERESTED_SOLUTION_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
              {purpose === OTHER_SOLUTION && (
                <Input aria-label="Other purpose" wrapperClassName={form.input} placeholder="Enter your purpose"
                  value={otherText} onChange={(e) => setOtherText(e.target.value)} />
              )}
            </div>
          )}
        </Field>

        {onBack ? (
          <div className={authCta.actions}>
            <Button type="button" variant="secondary" size="lg" className={authCta.tall} startIcon={<ArrowLeft size={15} />} onClick={onBack}>
              Back
            </Button>
            <Button type="submit" size="lg" className={`${authCta.ctaGradient} ${authCta.grow}`} disabled={leadMut.isPending}>
              {leadMut.isPending ? 'Submitting…' : 'Continue'}
            </Button>
          </div>
        ) : (
          <Button type="submit" fullWidth size="lg" className={authCta.ctaGradient} disabled={leadMut.isPending}>
            {leadMut.isPending ? 'Submitting…' : 'Continue'}
          </Button>
        )}
      </form>
    </AuthShell>
  );
}
